import { useMemo, useRef } from 'react';
import { useFrame, useLoader } from '@react-three/fiber';
import * as THREE from 'three';

function makeStarTexture() {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.35, 'rgba(255,255,255,0.55)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  return new THREE.CanvasTexture(canvas);
}

function StarLayer({ count, radiusMin, radiusMax, size }) {
  const sprite = useMemo(() => makeStarTexture(), []);

  const { positions, colors } = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const violet = new THREE.Color('#b79aff');
    const tangerine = new THREE.Color('#ffb98a');
    const white = new THREE.Color('#dfe6ff');
    for (let i = 0; i < count; i++) {
      const dir = new THREE.Vector3(
        Math.random() * 2 - 1,
        Math.random() * 2 - 1,
        Math.random() * 2 - 1
      ).normalize();
      const r = radiusMin + Math.random() * (radiusMax - radiusMin);
      positions.set([dir.x * r, dir.y * r, dir.z * r], i * 3);
      const roll = Math.random();
      const c = roll > 0.92 ? tangerine : roll > 0.78 ? violet : white;
      colors.set([c.r, c.g, c.b], i * 3);
    }
    return { positions, colors };
  }, [count, radiusMin, radiusMax]);

  return (
    <points>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        <bufferAttribute attach="attributes-color" args={[colors, 3]} />
      </bufferGeometry>
      <pointsMaterial
        size={size}
        map={sprite}
        vertexColors
        transparent
        opacity={0.9}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        sizeAttenuation
      />
    </points>
  );
}

export default function Starfield({ mobile, reducedMotion }) {
  const groupRef = useRef();

  const milkyWay = useLoader(THREE.TextureLoader, '/textures/night-sky.webp');

  useFrame((_, delta) => {
    if (groupRef.current && !reducedMotion) {
      groupRef.current.rotation.y += delta * 0.004;
    }
  });

  return (
    <group ref={groupRef}>
      <mesh scale={[-1, 1, 1]}>
        <sphereGeometry args={[80, 48, 48]} />
        <meshBasicMaterial map={milkyWay} side={THREE.BackSide} color="#8a8fa8" />
      </mesh>
      <StarLayer count={mobile ? 1300 : 2600} radiusMin={26} radiusMax={48} size={0.16} />
      <StarLayer count={mobile ? 400 : 900} radiusMin={18} radiusMax={30} size={0.1} />
    </group>
  );
}
